import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

export interface LabelDimensions {
  width: number;
  height: number;
  unit: 'in' | 'mm';
  lastUpdated?: number;
}

interface LabelSettingsContextType {
  dimensions: LabelDimensions;
  updateDimensions: (newDimensions: LabelDimensions) => void;
  getDimensionsInPixels: () => { width: number; height: number };
  getDimensionsForPrint: () => string;
}

const DEFAULT_DIMENSIONS: LabelDimensions = {
  width: 2.4,
  height: 1.1,
  unit: 'in',
  lastUpdated: Date.now()
};

const LabelSettingsContext = createContext<LabelSettingsContextType | undefined>(undefined);

const loadDimensionsFromStorage = (): LabelDimensions => {
  try {
    const savedSize = localStorage.getItem('labelSize');
    if (savedSize) {
      const parsed = JSON.parse(savedSize);
      // Validate that we have valid numeric values
      if (typeof parsed.width === 'number' && typeof parsed.height === 'number' && 
          parsed.width > 0 && parsed.height > 0 &&
          (parsed.unit === 'in' || parsed.unit === 'mm')) {
        return {
          width: parsed.width,
          height: parsed.height,
          unit: parsed.unit,
          lastUpdated: parsed.lastUpdated || Date.now()
        };
      }
    }
  } catch (error) {
    console.error('❌ Error parsing saved dimensions:', error);
  }
  // Only return default if localStorage is empty or invalid
  return DEFAULT_DIMENSIONS;
};

export const LabelSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [dimensions, setDimensions] = useState<LabelDimensions>(() => {
    const loaded = loadDimensionsFromStorage();
    console.log('🚀 LabelSettingsProvider initialized with dimensions:', loaded);
    console.log('🔍 localStorage current value:', localStorage.getItem('labelSize'));
    return loaded;
  });

  // Listen for storage changes from other tabs/components
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'labelSize' && e.newValue) {
        try {
          const newDimensions = JSON.parse(e.newValue);
          console.log('📱 Storage event detected, updating dimensions:', newDimensions);
          setDimensions(newDimensions);
        } catch (error) {
          console.error('❌ Error parsing storage event:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const updateDimensions = useCallback((newDimensions: LabelDimensions) => {
    const dimensionsWithTimestamp = {
      ...newDimensions,
      lastUpdated: Date.now()
    };
    
    console.log('🔄 Context updating dimensions to:', dimensionsWithTimestamp);
    
    try {
      // Save to localStorage
      localStorage.setItem('labelSize', JSON.stringify(dimensionsWithTimestamp));
      
      // Update state immediately
      setDimensions(dimensionsWithTimestamp);
      
      console.log('✅ Context dimensions updated successfully');
    } catch (error) {
      console.error('❌ Error updating dimensions in context:', error);
    }
  }, []);

  const getDimensionsInPixels = useCallback(() => {
    // Convert to pixels for web display (96 DPI for inches, 3.78 for mm)
    const widthPx = dimensions.unit === 'in' ? dimensions.width * 96 : dimensions.width * 3.78;
    const heightPx = dimensions.unit === 'in' ? dimensions.height * 96 : dimensions.height * 3.78;
    return { width: widthPx, height: heightPx };
  }, [dimensions]);

  const getDimensionsForPrint = useCallback(() => {
    return `${dimensions.width}${dimensions.unit} ${dimensions.height}${dimensions.unit}`;
  }, [dimensions]);

  const value = {
    dimensions,
    updateDimensions,
    getDimensionsInPixels,
    getDimensionsForPrint
  };

  return (
    <LabelSettingsContext.Provider value={value}>
      {children}
    </LabelSettingsContext.Provider>
  );
};

export const useLabelSettings = (): LabelSettingsContextType => {
  const context = useContext(LabelSettingsContext);
  if (context === undefined) {
    throw new Error('useLabelSettings must be used within a LabelSettingsProvider');
  }
  return context;
};